import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface InlineNoteEditorProps {
  value: string | null;
  onSave: (notes: string) => Promise<void>;
  maxLength?: number;
  placeholder?: string;
}

export default function InlineNoteEditor({
  value,
  onSave,
  maxLength = 500,
  placeholder,
}: InlineNoteEditorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [isSaving, setIsSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { t } = useTranslation();
  const placeholderText = placeholder || t('inlineNoteEditor.placeholder');

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.selectionStart = textareaRef.current.value.length;
    }
  }, [isEditing]);

  const handleSave = async () => {
    const trimmedValue = editValue.trim();

    // Only save if value changed
    if (trimmedValue === (value || '').trim()) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(trimmedValue);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save note:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value || '');
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCancel();
    } else if (e.key === 'Enter' && e.ctrlKey) {
      handleSave();
    }
  };

  if (isEditing) {
    return (
      <div className="mt-2">
        <textarea
          ref={textareaRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value.slice(0, maxLength))}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          disabled={isSaving}
          rows={3}
          className="w-full px-3 py-2 text-sm border border-blue-300 dark:border-blue-600 rounded-lg
                     bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                     focus:outline-none focus:ring-2 focus:ring-blue-500
                     disabled:opacity-50 resize-none"
          placeholder={placeholderText}
        />
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {t('inlineNoteEditor.charCount', { current: editValue.length, max: maxLength })}
          </span>
          {isSaving && (
            <span className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1">
              <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              {t('inlineNoteEditor.saving')}
            </span>
          )}
        </div>
      </div>
    );
  }

  if (value) {
    return (
      <div
        onClick={() => setIsEditing(true)}
        className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg cursor-pointer
                   hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors group"
      >
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm text-yellow-800 dark:text-yellow-200 whitespace-pre-wrap">
            {value}
          </p>
          <svg
            className="w-4 h-4 text-yellow-600 dark:text-yellow-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={() => setIsEditing(true)}
      className="mt-2 flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400
                 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
      {placeholderText}
    </button>
  );
}
